/**
 * ExistingClientIntakeForm — lookup + confirm flow for existing clients.
 * ────────────────────────────────────────────────────────────────────
 * Steps: type-ahead search → pick → confirm address (editable) → same/new
 * property radio → (if new) property fields → preferred_quote_types multi-
 * select → save. Auto-save draft as we go.
 *
 * Iter303ag-fix8 rebuild:
 *   • Live type-ahead search (300 ms debounce) — no more Search button.
 *     Sends `?search=<q>` (was `?q=` which the backend silently ignored).
 *     Matches against name_first, name_last, company_name, client_no,
 *     phone_mobile, email, address_suburb + street + city.
 *   • Auto-scrolls the picked client into view and clears the result list.
 *   • All 5 prompts are inline-editable via pencil (`InlineParamEdit`),
 *     wired through the `editCtx` prop from OpenIntakeForm.
 *   • Service Preference two-step replaced by the canonical 8-item work-
 *     type multi-select, matching New Client Action 5 (Re-Roof · New
 *     Roof · Maintenance · Remedial Repairs · Re-Coat · Repairs ·
 *     Restoration · Guttering). Operator can skip if just booking an
 *     appraisal.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Loader2, Save, Search, CheckCircle2, X } from 'lucide-react';
import { FieldRowControls, advanceOnEnter } from './scriptEditControls';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

function parsePromptScript(promptsText) {
  const list = [];
  if (!promptsText) return list;
  promptsText.split('\n').forEach(line => {
    const m = line.match(/^([a-z0-9_]+):\s*(.+)$/i);
    if (m) list.push({ key: m[1], prompt: m[2].trim() });
  });
  return list;
}

function substitute(template, data) {
  if (!template) return '';
  return template.replace(/\{([^}]+)\}/g, (_, key) => {
    const parts = key.split('.');
    let v = data;
    for (const p of parts) v = v?.[p];
    return v != null ? String(v) : `{${key}}`;
  });
}

/**
 * EditablePrompt — inline-editable "💬 Say to caller" bubble. Uses the
 * shared FieldRowControls so the pencil PATCHes the parent action's
 * `prompts` param exactly like PropertyIntakeForm's per-field prompts.
 * When editCtx is null (runner not in edit mode) or the key isn't in
 * the script (shouldn't happen), falls back to plain read-only text.
 */
function EditablePrompt({ script, promptKey, editCtx, resolvedText, fallback }) {
  const idx = script.findIndex(s => s.key === promptKey);
  const item = idx >= 0 ? script[idx] : null;
  const displayText = resolvedText || item?.prompt || fallback || '';
  if (!displayText && !editCtx) return null;
  return (
    <div className="group flex items-start justify-between gap-2 mb-1">
      <div className="text-sm text-emerald-700 italic" data-testid={`ex-client-prompt-${promptKey}`}>
        💬 &ldquo;{displayText}&rdquo;
      </div>
      {editCtx && item && (
        <FieldRowControls
          editCtx={editCtx}
          items={script}
          idx={idx}
          currentPrompt={item.prompt}
          canReorder={false}
        />
      )}
    </div>
  );
}

// (iter303ag-fix9 — the canonical work-type multi-select moved into the
// downstream create_new_property_form action, so this constant is no
// longer referenced from here. Left commented as a reminder that the
// canonical list is in PropertyDetailPage.js + /dropdowns/preferred_quote_types.)

export default function ExistingClientIntakeForm({ prompts, capturedData, onSave, onCancel, autoSaveDraft, editCtx }) {
  const script = useMemo(() => parsePromptScript(prompts), [prompts]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [client, setClient] = useState(null);
  const [addressConfirm, setAddressConfirm] = useState('');
  const [sameProperty, setSameProperty] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);
  const autosaveTimer = useRef(null);
  const debounceTimer = useRef(null);
  const pickedRef = useRef(null);

  // Draft snapshot for autosave
  const draft = useMemo(() => ({
    client_id: client?.id || client?._id,
    client, addressConfirm, sameProperty,
  }), [client, addressConfirm, sameProperty]);

  useEffect(() => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      if (autoSaveDraft && client) {
        autoSaveDraft(draft);
        setSavedAt(new Date());
      }
    }, 3000);
    return () => clearTimeout(autosaveTimer.current);
  }, [draft, autoSaveDraft, client]);

  // Type-ahead debounce (300 ms). Fires the actual search after the
  // operator pauses typing — avoids hammering the backend on every
  // keystroke. Setting query to empty clears the list immediately.
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    const q = query.trim();
    if (!q) { setResults([]); setShowResults(false); setSearching(false); return; }
    setSearching(true);
    debounceTimer.current = setTimeout(async () => {
      try {
        const res = await api.get('/clients', { params: { search: q, limit: 8 } });
        const rows = Array.isArray(res.data) ? res.data : (res.data?.items || []);
        setResults(rows.slice(0, 8));
        setShowResults(true);
      } catch (e) {
        setResults([]);
        setShowResults(false);
      } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(debounceTimer.current);
  }, [query]);

  const pickClient = (c) => {
    setClient(c);
    // Pre-fill the address confirmation with what we have on file
    const currentAddr = [c.address_num, c.address_street, c.address_suburb, c.address_city].filter(Boolean).join(' ');
    setAddressConfirm(currentAddr);
    // Clear the result list + auto-scroll the selected client into view.
    setShowResults(false);
    setResults([]);
    setQuery('');
    setTimeout(() => {
      if (pickedRef.current) {
        pickedRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 50);
  };

  // iter303ak-fix52 · If the runner started with a pre-picked client_id
  // (operator clicked "Existing Client" on the Client Card / Runner Home),
  // auto-load that client and skip the search step. Only runs once on
  // mount when we don't already have a client but do have an id.
  useEffect(() => {
    const preId = capturedData?.client_id || capturedData?.client_record?.id || capturedData?.client_record?._id;
    if (!preId || client) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/clients/${preId}`);
        if (!cancelled && res.data) pickClient(res.data);
      } catch { /* silent — operator can search manually */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearSearch = () => {
    setQuery(''); setResults([]); setShowResults(false); setSearching(false);
  };

  // Iter303ag-fix9 — Existing Client now defers the "new property"
  // intake to a downstream conditional `create_new_property_form`
  // action (mirrors New Client Action 6). This form only captures:
  //   client → confirm address → same/new property radio.
  // Preferred work types + property intake happen in the conditional
  // action, which reuses the exact same primitive New Client uses.
  const canSubmit = client && sameProperty;

  const submit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) { setError('Please complete all sections.'); return; }
    setSaving(true); setError('');
    try {
      // Update client's address if changed
      const currentAddr = [client.address_num, client.address_street, client.address_suburb, client.address_city].filter(Boolean).join(' ');
      if (addressConfirm && addressConfirm !== currentAddr) {
        await api.patch(`/clients/${client.id || client._id}`, { address_notes: `Confirmed on runner: ${addressConfirm}` }).catch(() => {});
      }
      onSave({
        client_id: client.id || client._id,
        client_record: client,
        // Iter303ag-fix9 — top-level `same_property` written to
        // captured_data so the downstream conditional
        // create_new_property_form action's trigger_when
        // (`same_property == no`) resolves correctly.
        same_property: sameProperty,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      let msg = e?.message || 'Save failed';
      if (Array.isArray(detail)) msg = detail.map(d => d?.msg || String(d)).join('\n');
      else if (typeof detail === 'string') msg = detail;
      setError(msg);
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} onKeyDown={advanceOnEnter} className="space-y-6">
      <div className="bg-gradient-to-br from-emerald-50 to-white border-l-4 border-emerald-500 rounded-r-lg p-4">
        <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold mb-1">🔍 Existing Client Lookup</div>
        <div className="text-slate-700">Find the client, confirm their details, and capture what they need.</div>
      </div>

      {/* Step 1: Type-ahead Search */}
      <section className="border-l-2 border-slate-100 pl-4">
        <EditablePrompt
          script={script}
          promptKey="client_search"
          editCtx={editCtx}
          fallback="Can I grab your name, business or address to pull up your file?"
        />
        <label className="block text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Client search *</label>
        <div className="relative">
          <input value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => { if (results.length) setShowResults(true); }}
            placeholder="Search by name, phone or client #…"
            data-testid="existing-search-input"
            autoComplete="off"
            className="w-full text-sm pl-9 pr-10 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-300" />
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          {searching && (
            <Loader2 size={14} className="absolute right-9 top-1/2 -translate-y-1/2 animate-spin text-emerald-500" />
          )}
          {query && !searching && (
            <button type="button" onClick={clearSearch}
              data-testid="existing-search-clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X size={14} />
            </button>
          )}
        </div>
        {showResults && results.length > 0 && (
          <div className="border border-slate-200 rounded mt-2 divide-y divide-slate-100 bg-white max-h-56 overflow-y-auto shadow-sm">
            {results.map((r, i) => {
              const id = r.id || r._id || i;
              const clientNo = r.client_no ? `C${String(r.client_no).replace(/^C/i,'')}` : '';
              return (
                <button type="button" key={id} onClick={() => pickClient(r)}
                  data-testid={`existing-result-${i}`}
                  className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-sm">
                  <div className="flex items-center gap-2">
                    {clientNo && <span className="font-mono text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">{clientNo}</span>}
                    <b>{r.name_first} {r.name_last}</b>
                    {r.company_name && <span className="text-slate-500">· {r.company_name}</span>}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {(r.phone_mobile || r.phone_home) && <span>{r.phone_mobile || r.phone_home}</span>}
                    {r.phone_mobile || r.phone_home ? ' · ' : ''}
                    {[r.address_num, r.address_street, r.address_suburb].filter(Boolean).join(' ') || <em className="text-slate-400">no address</em>}
                  </div>
                </button>
              );
            })}
          </div>
        )}
        {showResults && !results.length && !searching && (
          <div className="text-[11px] text-slate-400 italic mt-2">No matches for &ldquo;{query}&rdquo;.</div>
        )}
        {client && (
          <div ref={pickedRef} className="mt-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-2 flex items-center justify-between">
            <span>✓ Selected: <b>{client.name_first} {client.name_last}</b>{client.client_no && <span className="text-emerald-600 ml-1">· C{String(client.client_no).replace(/^C/i,'')}</span>}</span>
            <button type="button" onClick={() => { setClient(null); setAddressConfirm(''); }}
              data-testid="existing-unselect-btn"
              className="text-[10px] uppercase tracking-wider text-emerald-700 hover:text-emerald-900 underline">
              Change
            </button>
          </div>
        )}
      </section>

      {client && (
        <>
          {/* Step 2: Confirm address */}
          <section className="border-l-2 border-slate-100 pl-4">
            <EditablePrompt
              script={script}
              promptKey="address_confirm"
              editCtx={editCtx}
              fallback="Just to confirm — is your address still {client.home_address}?"
              resolvedText={substitute((script.find(s => s.key === 'address_confirm') || {}).prompt || 'Just to confirm — is your address still {client.home_address}?', { client: { home_address: [client.address_num, client.address_street, client.address_suburb, client.address_city].filter(Boolean).join(' ') } })}
            />
            <label className="block text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Confirmed address</label>
            <input value={addressConfirm} onChange={e => setAddressConfirm(e.target.value)}
              data-testid="existing-address-input"
              className="w-full text-sm px-3 py-2 border border-slate-300 rounded" />
          </section>

          {/* Step 3: Same or new property */}
          <section className="border-l-2 border-slate-100 pl-4">
            <EditablePrompt
              script={script}
              promptKey="same_property"
              editCtx={editCtx}
              fallback="Is this the property you'd like appraised?"
            />
            <div className="flex gap-2">
              {['yes', 'no'].map(opt => (
                <button key={opt} type="button"
                  onClick={() => setSameProperty(opt)}
                  data-testid={`existing-same-property-${opt}`}
                  className={`px-4 py-2 border-2 rounded font-semibold text-sm ${sameProperty === opt ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-emerald-300'}`}>
                  {opt === 'yes' ? 'Same property' : 'New property'}
                </button>
              ))}
            </div>
            {sameProperty === 'no' && (
              <div className="mt-3 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-3 italic">
                We&apos;ll capture the new property address, roof details and preferred work types in the next step.
              </div>
            )}
          </section>
        </>
      )}

      {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3 whitespace-pre-wrap">{error}</div>}

      <div className="flex items-center gap-3 sticky bottom-0 bg-white py-3 border-t border-slate-100">
        <div className="text-xs text-slate-400">
          {savedAt && (
            <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-500" /> Draft auto-saved</span>
          )}
        </div>
        <span className="flex-1" />
        <button type="button" onClick={onCancel}
          data-testid="existing-cancel-btn"
          className="px-4 py-2 text-slate-500 hover:text-slate-700">Cancel</button>
        <button type="submit" disabled={saving || !canSubmit}
          data-testid="existing-save-btn"
          className="px-6 py-2 bg-emerald-600 text-white font-semibold rounded hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-2">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Confirm &amp; Continue
        </button>
      </div>
    </form>
  );
}
